import { SubmitEvent, useEffect } from 'react'
import { Title } from '@shared/ui/Typography/Title'
import s from './RegistrationForm.module.sass'
import { useField } from '@shared/lib/fields/hooks/useField/useField'

import {
  getField,
  INN_MASKS,
  INN_VALIDATIONS,
  OrgTypes,
  PlaceFormFields,
} from '@pages/OwnerLanding/RegistrationForm/Forms/fields'

import { BuildInput } from '@shared/ui/Form/Build/BuildInput'
import { Button } from '@shared/ui/Actions/Button'
import { BuildSelect } from '@shared/ui/Form/Build/BuildSelect'
import { useAppSelector } from '@app/model/store/client'
import { BuildRadio } from '@shared/ui/Form/Build/BuildRadio'
import { IconButton } from '@shared/ui/Actions/IconButton'
import IconArrow from '@icons/icon-arrow-quick-filter.svg'
import { RadioOption } from '@shared/types/fields/fields'

interface Props {
  defaultState: Partial<Record<PlaceFormFields, string>>
  onPrevClick: () => void
  onSubmit?: (state: Partial<Record<PlaceFormFields, string>>) => void
}

export const PlaceInfoForm = ({ defaultState, onSubmit, onPrevClick }: Props) => {
  const storeCityList = useAppSelector((store) => store.global.cityList)

  const handleSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.stopPropagation()
    e.preventDefault()
    onSubmit &&
      onSubmit({
        [PlaceFormFields.name_establishment]: establishmentName.value,
        [PlaceFormFields.address]: address.value,
        [PlaceFormFields.social_link]: socialLink.value,
        [PlaceFormFields.type_organisation]: orgType.value,
        [PlaceFormFields.inn]: inn.value.replace(/\D/g, ''),
        [PlaceFormFields.kpp]: kpp.value,
        [PlaceFormFields.name_organisation]: orgName.value,
        [PlaceFormFields.city]: city.value?.id,
      })
  }

  const [establishmentName, setEstablishmentName] = useField(
    getField(PlaceFormFields.name_establishment, defaultState[PlaceFormFields.name_establishment]),
  )

  const [city, setCity] = useField(
    getField(
      PlaceFormFields.city,
      defaultState[PlaceFormFields.city]
        ? {
            id: defaultState[PlaceFormFields.city],
            value: (storeCityList as unknown as Record<string, { name: string }>)?.[defaultState[PlaceFormFields.city]!]
              ?.name,
            label: (storeCityList as unknown as Record<string, { name: string }>)?.[defaultState[PlaceFormFields.city]!]
              ?.name,
            disabled: false,
          }
        : undefined,
    ),
  )

  const [address, setAddress] = useField(getField(PlaceFormFields.address, defaultState[PlaceFormFields.address]))

  const [socialLink, setSocialLink] = useField(
    getField(PlaceFormFields.social_link, defaultState[PlaceFormFields.social_link]),
  )

  const [orgType, setOrgType] = useField(
    getField(PlaceFormFields.type_organisation, defaultState[PlaceFormFields.type_organisation]),
  )

  const initialOrgType = (defaultState[PlaceFormFields.type_organisation] as OrgTypes) || OrgTypes.OOO
  const innMask = INN_MASKS[initialOrgType]
  const baseInnField = getField(PlaceFormFields.inn)

  const [inn, setInn] = useField({
    ...baseInnField,
    value: defaultState[PlaceFormFields.inn] ? innMask(String(defaultState[PlaceFormFields.inn])) : baseInnField.value,
    validations: {
      required: baseInnField.validations.required,
      ...INN_VALIDATIONS[initialOrgType],
    },
    settings: {
      ...baseInnField.settings,
      mask: innMask,
    },
  })

  const [kpp, setKpp] = useField(getField(PlaceFormFields.kpp, defaultState[PlaceFormFields.kpp]))

  const [orgName, setOrgName] = useField(
    getField(PlaceFormFields.name_organisation, defaultState[PlaceFormFields.name_organisation]),
  )

  const handleOrgTypeChange = (value: RadioOption) => {
    // валидация инн зависит от типа организации
    console.log(value)
    const nextMask = INN_MASKS[value.id as OrgTypes]
    setInn((oldV) => ({
      ...oldV,
      value: nextMask(oldV.value),
      validations: {
        required: oldV.validations.required,
        ...INN_VALIDATIONS[value.id as OrgTypes],
      },
      settings: {
        ...oldV.settings,
        mask: nextMask,
      },
    }))
  }

  // сет пула городов для ситипикера
  useEffect(() => {
    setCity((oldV) => ({
      ...oldV,
      settings: {
        ...oldV.settings,
        options: storeCityList?.map((item) => ({
          id: item.id,
          value: item.name,
          label: item.name,
          disabled: false,
        })),
      },
    }))
  }, [])

  const isSubmitAllowed = [establishmentName, city, orgType, inn, orgName, kpp].reduce((acc, field) => {
    return acc && !!field?.valid
  }, true)

  return (
    <form className={s.form} onSubmit={handleSubmit}>
      <Title tag='h1' sizes='h1 h2' className={s.title}>
        Расскажите о заведении
      </Title>

      <div className={s.content}>
        <BuildInput field={establishmentName} setField={setEstablishmentName} />

        <div className={s.row}>
          <BuildSelect field={city} setField={setCity} />
          <BuildInput field={address} setField={setAddress} />
        </div>

        <BuildInput field={socialLink} setField={setSocialLink} />

        <div className={s.row}>
          <BuildRadio field={orgType} setField={setOrgType} handleChange={handleOrgTypeChange} />
          <BuildInput field={inn} setField={setInn} />
        </div>

        <div className={s.row}>
          <BuildInput field={kpp} setField={setKpp} />
          <BuildInput field={orgName} setField={setOrgName} />
        </div>
      </div>

      <div className={s['buttons-row']}>
        <IconButton
          className={s['prev-button']}
          icon={IconArrow}
          sizes='XL'
          mode='white-border'
          onClick={onPrevClick}
          type='button'
        />

        <Button type='submit' className={s['next-button']} sizes='XL' mode='black' disabled={!isSubmitAllowed}>
          Далее
        </Button>
      </div>
    </form>
  )
}
